import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Loader2, Plus, Save, Check, Trash2, Wallet } from 'lucide-react';
import { EDITABLE_POSTES, POSTE_LABELS } from '../../../constants/chargePostes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fcfa = (n) => `${fmt(n)} FCFA`;

const STATUS = {
  pending: { label: 'En attente de remboursement', className: 'border-amber-300 text-amber-700 bg-amber-50' },
  reimbursed: { label: 'Remboursé', className: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  written_off: { label: 'Abandonné', className: 'border-gray-300 text-gray-500 bg-gray-50' },
};

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  poste: 'fuel',
  amount: '',
  financedBy: 'client',
  reference: '',
  notes: '',
});

const ContractPrefinancingTab = ({ contractId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contract-margin/${contractId}/prefinancings`);
      setItems(res.data?.items || []);
      setTotals(res.data?.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement des préfinancements');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const create = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Montant obligatoire');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/contract-margin/${contractId}/prefinancings`, {
        date: form.date,
        poste: form.poste,
        amount: Number(form.amount),
        financedBy: form.financedBy,
        reference: form.reference || null,
        notes: form.notes || null,
      });
      toast.success('Préfinancement enregistré');
      setDialogOpen(false);
      setForm(emptyForm());
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const markReimbursed = async (item) => {
    try {
      await axios.patch(`${API}/admin/contract-margin/prefinancings/${item.id}`, {
        status: 'reimbursed',
      });
      toast.success('Marqué comme remboursé');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/admin/contract-margin/prefinancings/${item.id}`);
      toast.success('Supprimé');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="prefinancing-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-prefinancing-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="prefi-kpi-baobab-pending">
          <p className="text-[11px] uppercase tracking-wider text-amber-700 font-medium">
            Avancé par nous, à récupérer
          </p>
          <p className="text-xl font-bold font-mono text-amber-900 mt-1">{fcfa(totals.baobabPending)}</p>
          <p className="text-[10px] text-amber-700 mt-1">Compté comme coût dans la marge</p>
        </div>
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="prefi-kpi-client">
          <p className="text-[11px] uppercase tracking-wider text-sky-700 font-medium">
            Préfinancé par le client
          </p>
          <p className="text-xl font-bold font-mono text-sky-900 mt-1">{fcfa(totals.clientTotal)}</p>
          <p className="text-[10px] text-sky-700 mt-1">Hors marge — dont {fcfa(totals.clientPending)} non soldés</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="prefi-kpi-reimbursed">
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium">
            Nos avances remboursées
          </p>
          <p className="text-xl font-bold font-mono text-emerald-900 mt-1">{fcfa(totals.baobabReimbursed)}</p>
          <p className="text-[10px] text-emerald-700 mt-1">Neutre dans la marge</p>
        </div>
      </div>

      <Card data-testid="prefinancing-list-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-gray-500" /> Suivi des charges préfinancées
            </span>
            <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="add-prefinancing-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Enregistrer une avance
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="prefinancing-table">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 px-3">Poste</th>
                  <th className="text-left py-2 px-3">Préfinancé par</th>
                  <th className="text-right py-2 px-3">Montant</th>
                  <th className="text-left py-2 px-3">Statut</th>
                  <th className="text-right py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-gray-400 text-sm">
                    Aucune charge préfinancée enregistrée
                  </td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-gray-50" data-testid={`prefinancing-row-${it.id}`}>
                    <td className="py-2 pr-3 font-mono">{it.date}</td>
                    <td className="py-2 px-3">{it.posteLabel || POSTE_LABELS[it.poste] || it.poste}</td>
                    <td className="py-2 px-3">
                      {it.financedBy === 'baobab' ? 'Baobab' : 'Client'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">{fcfa(it.amount)}</td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className={`text-[10px] ${STATUS[it.status]?.className || ''}`}>
                        {STATUS[it.status]?.label || it.status}
                      </Badge>
                    </td>
                    <td className="py-2 pl-3 text-right">
                      <span className="flex items-center justify-end gap-1">
                        {it.status === 'pending' && (
                          <Button size="sm" variant="ghost" onClick={() => markReimbursed(it)}
                                  data-testid={`reimburse-${it.id}`}>
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => remove(it)}
                                data-testid={`delete-prefinancing-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="prefinancing-dialog">
          <DialogHeader>
            <DialogTitle>Enregistrer une charge préfinancée</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} className="mt-1 font-mono h-9"
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="prefi-date-input" />
            </div>
            <div>
              <Label className="text-xs">Poste préfinancé</Label>
              <Select value={form.poste} onValueChange={(v) => setForm({ ...form, poste: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="prefi-poste-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDITABLE_POSTES.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Préfinancé par</Label>
              <Select value={form.financedBy} onValueChange={(v) => setForm({ ...form, financedBy: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="prefi-financedby-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Le client (hors marge)</SelectItem>
                  <SelectItem value="baobab">Baobab (coût tant que non remboursé)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Montant (FCFA)</Label>
              <Input type="number" value={form.amount} className="mt-1 font-mono h-9" placeholder="0"
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     data-testid="prefi-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Référence (bon, reçu, facture…)</Label>
              <Input value={form.reference} className="mt-1 h-9"
                     onChange={(e) => setForm({ ...form, reference: e.target.value })}
                     data-testid="prefi-reference-input" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} className="mt-1 text-sm"
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="prefi-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={create} disabled={saving} data-testid="save-prefinancing-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractPrefinancingTab;
